import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { List, Card, Skeleton, Table } from "antd";
import { useGetUserInfo } from "../../../hooks/useGetUserInfo";
import { routePath } from "../../../config/routeMgt/routePath";
import { useGetTalentCount } from "../../home/hooks/useGetTalentCount";
import { useQueryClient } from "react-query";
import { openNotification } from "../../../utils/notification";
import { useBookmarkJob } from "../hooks/useBookmark";
import { useLikeJob } from "../hooks/useLikeJob";
import { trackMetaJobLeadForListing } from "../../../utils/metaPixelJob";
import { Job, useGetSuggestedJobs } from "../hooks/useGetSuggestedJobs";
import { formatDate } from "../../../admin/Authentication/hooks/useDateFormat";



interface SuggestedJobsTableProps {
  searchTerm: string;
  handleJob: (id: number) => void;
}

export const QUERY_KEY_FOR_SUGGESTED_JOBS = "suggested-jobs";

export const SuggestedJobsTable: React.FC<SuggestedJobsTableProps> = ({
  searchTerm,
  handleJob
}) => {
  const [loading, setLoading] = useState(true);
  const { data: profileData } = useGetTalentCount();
  const { token } = useGetUserInfo();
  const { mutate, isLoading: loadings } = useLikeJob();
  const { mutate: bookmarkMutate } = useBookmarkJob();
  const queryClient = useQueryClient();

  const stripHtmlTags = (html: string): string => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  const formatExperienceLevel = (level: string): string => {
    return level
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const likeJob = (record: any) => {
    const id = record.id;
    const hasLiked = record.has_liked;
    if (!isProfileComplete) {
      openNotification({
        title: "Incomplete Profile",
        description: "You need at least 75% profile completion to like jobs.",
        state: "error",
        duration: 4.5,
      });
      return;
    }

    const props: any = {
      token,
      id: id,
    };

    mutate(props, {
      onError: (err: any) => {
        let errorTitle = "Error";
        let errorDescription = "An error occurred, please try again.";

        if (err.response && err.response.data && err.response.data.error) {
          errorTitle = "Error: " + err.response.data.error;
          errorDescription =
            err.response.data.description || "Please try again.";
        }

        openNotification({
          title: errorTitle,
          state: "error",
          description: errorDescription,
          duration: 6,
        });
      },
      onSuccess: (res: any) => {
        openNotification({
          title: "Success",
          state: "success",
          description: hasLiked
            ? "Job unliked successfully."
            : "Job liked successfully.",
          duration: 4.5,
        });
        if (!hasLiked) {
          trackMetaJobLeadForListing({
            id: record.id,
            slug: record.slug,
            title: record.title,
          });
        }
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_FOR_SUGGESTED_JOBS],
        });
      },
    });
  };


  const { data, isLoading, isError } = useGetSuggestedJobs();


  useEffect(() => {
    if (isLoading) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [isLoading]);

  const isProfileComplete = profileData?.profile.total >= 0;
  // const isProfileComplete = profileData?.profile.total >= 75;

  const handleBookmark = (id: any) => {
    if (!isProfileComplete) {
      openNotification({
        title: "Incomplete Profile",
        description: "You need at least 75% profile completion to bookmark jobs.",
        state: "error",
        duration: 4.5,
      });
      return;
    }

    const props: any = {
      token,
      id: id,
    };

    bookmarkMutate(props, {
      onError: (err: any) => {
        let errorTitle = "Error";
        let errorDescription = "An error occurred, please try again.";

        if (err.response && err.response.data && err.response.data.error) {
          errorTitle = "Error: " + err.response.data.error;
          errorDescription =
            err.response.data.description || "Please try again.";
        }

        openNotification({
          title: errorTitle,
          state: "error",
          description: errorDescription,
          duration: 6,
        });
      },
      onSuccess: (res: any) => {
        openNotification({
          title: "Success",
          state: "success",
          description: "Job bookmark updated successfully.",
          duration: 4.5,
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_FOR_SUGGESTED_JOBS],
        });
      },
    });
  };

  const columns = [
    {
      title: "Job Title",
      dataIndex: "title",
      render: (_: any, record: any) => (
        <Link
          to={routePath.jobDetails(record.id).path}
          className="font-medium text-primary block"
        >
          {record.title}
        </Link>
      ),
    },
    {
      title: "Organization",
      dataIndex: "company_name",
    },
    {
      title: "Location",
      render: (_: any, record: any) => (
        <span className="capitalize">
          {record.location_city}, {record.location_country}
        </span>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (type: any) => <span className="capitalize">{type}</span>,
    },
    {
      title: "Experience Level",
      dataIndex: "level",
      render: (level: any) => (
        <span className="capitalize">{formatExperienceLevel(level)}</span>
      ),
    },
    {
      title: "Date Posted",
      dataIndex: "created_at",
      render: (text: any) => {
        const date = new Date(text);
        return formatDate(date);
      },
    },
    {
      title: "Expiry Date",
      dataIndex: "expiry_date",
      render: (text: any) => formatDate(new Date(text)),
    },
    {
      title: "Action",
      render: (_: any, record: any) => (
        <div className="flex items-center gap-2">
          <i
            onClick={() => likeJob(record)}
            className={`${record.has_liked
              ? "ri-heart-fill text-red-500"
              : "ri-heart-line"
              } text-lg cursor-pointer`}
            style={{
              pointerEvents: isProfileComplete ? "auto" : "none",
              opacity: isProfileComplete ? 1 : 0.5,
            }}
          ></i>
          <i
            onClick={() => handleBookmark(record.id)}
            className={`${record.has_bookmarked
              ? "ri-bookmark-fill text-primary"
              : "ri-bookmark-line"
              } text-lg cursor-pointer`}
            style={{
              pointerEvents: isProfileComplete ? "auto" : "none",
              opacity: isProfileComplete ? 1 : 0.5,
            }}
          ></i>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <List
        grid={{ gutter: 16, column: 2 }}
        dataSource={[1, 2, 3, 4]}
        renderItem={() => (
          <List.Item>
            <Card>
              <Skeleton active />
            </Card>
          </List.Item>
        )}
      />
    );
  }



  return (
    <Table
      className="bg-white rounded-md shadow border mt-8"
      columns={columns}
      dataSource={data?.data.map((item: Job) => ({ ...item, key: item.id }))}
      loading={isLoading}
      scroll={{ x: 550 }}
      pagination={{
        total: data?.total,
        showSizeChanger: true,
      }}
    />
  );
};